import { useState } from "react";
import { useAuth } from "@/hooks/use-auth";
import { 
  useListAdminEngineerJobs, 
  useListEngineers, 
  useListAdminSites,
  useCreateAdminEngineerJob,
  useUpdateAdminEngineerJob,
  Job,
  getGetEngineerJobQueryKey,
  getListAdminEngineerJobsQueryKey
} from "@workspace/api-client-react";
import { Loader2, Plus, Calendar, AlertTriangle, Search, Filter, Wrench, Check, Map, RefreshCw, X, ChevronRight, CheckCircle } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { Link, useLocation } from "wouter";
import { ChevronLeft, LogOut } from "lucide-react";
import { logout } from "@/hooks/use-auth";
import { useQueryClient } from "@tanstack/react-query";
import { BRANDS } from "@/lib/constants";

export default function AdminEngineerJobs() {
  const { data: user, isLoading: isAuthLoading } = useAuth();
  const [, setLocation] = useLocation();
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const { data: jobs, isLoading: isJobsLoading, refetch: refetchJobs, isError: isJobsError } = useListAdminEngineerJobs({
    query: { queryKey: getListAdminEngineerJobsQueryKey(), enabled: user?.role === "admin", refetchInterval: 15000, staleTime: 0 }
  });
  const { data: engineers, isLoading: isEngineersLoading, isError: isEngineersError } = useListEngineers();
  const { data: sites, isLoading: isSitesLoading, isError: isSitesError } = useListAdminSites();
  
  const createJob = useCreateAdminEngineerJob();
  const updateJob = useUpdateAdminEngineerJob();

  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [kindFilter, setKindFilter] = useState("all");
  const [priorityFilter, setPriorityFilter] = useState("all");
  
  const [isCreating, setIsCreating] = useState(false);
  const [editingJob, setEditingJob] = useState<Job | null>(null);

  // Form states
  const [formData, setFormData] = useState({
    title: "",
    description: "",
    kind: "callout" as "callout" | "site_survey",
    priority: "normal" as "normal" | "urgent",
    scheduledDate: "",
    brand: "",
    engineerId: "",
    siteId: ""
  });

  const isLoading = isAuthLoading || isJobsLoading || isEngineersLoading || isSitesLoading;

  if (isLoading) {
    return <div className="min-h-screen bg-slate-50 flex justify-center p-8"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div>;
  }
  
  if (isJobsError || isEngineersError || isSitesError) {
    return (
      <div className="min-h-screen bg-slate-50 flex flex-col items-center justify-center p-4 text-center">
        <AlertTriangle className="w-12 h-12 text-red-500 mb-4" />
        <h2 className="text-xl font-bold text-slate-900 mb-2">Error Loading Data</h2>
        <p className="text-slate-600 mb-6">There was a problem loading the jobs dashboard.</p>
        <button onClick={() => window.location.reload()} className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors">Retry</button>
      </div>
    );
  }

  const activeEngineers = engineers?.filter(e => e.active) || [];
  const activeSites = sites?.filter(s => s.active) || [];

  const handleCreateOpen = () => {
    if (activeEngineers.length === 0 || activeSites.length === 0) {
      toast({ 
        title: "Cannot create job", 
        description: "You need at least one active engineer and one active site to assign a job.", 
        variant: "destructive" 
      });
      return;
    }
    
    setFormData({
      title: "",
      description: "",
      kind: "callout",
      priority: "normal",
      scheduledDate: new Date().toISOString().split('T')[0],
      brand: "",
      engineerId: activeEngineers[0].id.toString(),
      siteId: ""
    });
    setEditingJob(null);
    setIsCreating(true);
  };

  const handleEditOpen = (job: Job) => {
    setFormData({
      title: job.title,
      description: job.description,
      kind: job.kind,
      priority: job.priority,
      scheduledDate: job.scheduledDate,
      brand: job.site.brand || "",
      engineerId: job.engineerId.toString(),
      siteId: job.siteId.toString()
    });
    setEditingJob(job);
    setIsCreating(true);
  };

  const handleSave = () => {
    if (!formData.title || !formData.scheduledDate || !formData.engineerId || !formData.siteId) {
      toast({ title: "Validation Error", description: "Please fill all required fields", variant: "destructive" });
      return;
    }

    if (editingJob) {
      updateJob.mutate({
        id: editingJob.id,
        data: {
          title: formData.title,
          description: formData.description,
          engineerId: parseInt(formData.engineerId),
          scheduledDate: formData.scheduledDate,
          priority: formData.priority,
          version: editingJob.version
        }
      }, {
        onSuccess: () => {
          toast({ title: "Job updated successfully" });
          setIsCreating(false);
          setEditingJob(null);
          refetchJobs();
          queryClient.invalidateQueries({ queryKey: getGetEngineerJobQueryKey(editingJob.id) });
        },
        onError: (err: any) => {
          toast({ title: "Failed to update job", description: err.message, variant: "destructive" });
        }
      });
    } else {
      createJob.mutate({
        data: {
          title: formData.title,
          description: formData.description,
          kind: formData.kind,
          priority: formData.priority,
          scheduledDate: formData.scheduledDate,
          engineerId: parseInt(formData.engineerId),
          siteId: parseInt(formData.siteId)
        }
      }, {
        onSuccess: () => {
          toast({ title: "Job created successfully" });
          setIsCreating(false);
          refetchJobs();
        },
        onError: (err: any) => {
          toast({ title: "Failed to create job", description: err.message, variant: "destructive" });
        }
      });
    }
  };

  const handleStatusChange = (job: Job, newStatus: "cancelled" | "in_progress") => {
    updateJob.mutate({
      id: job.id,
      data: {
        version: job.version,
        status: newStatus
      }
    }, {
      onSuccess: () => {
        toast({ title: `Job ${newStatus === 'cancelled' ? 'cancelled' : 'reopened'}` });
        refetchJobs();
        queryClient.invalidateQueries({ queryKey: getGetEngineerJobQueryKey(job.id) });
      },
      onError: (err: any) => {
        toast({ title: "Failed to update status", description: err.message, variant: "destructive" });
      }
    });
  };

  const filteredJobs = jobs?.filter(job => {
    const matchesSearch = job.title.toLowerCase().includes(searchTerm.toLowerCase()) || 
                          job.site.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          job.engineerName.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === "all" || job.status === statusFilter;
    const matchesKind = kindFilter === "all" || job.kind === kindFilter;
    const matchesPriority = priorityFilter === "all" || job.priority === priorityFilter;
    return matchesSearch && matchesStatus && matchesKind && matchesPriority;
  }) || [];
  
  // Get selected site for preview
  const selectedSite = activeSites.find(s => s.id.toString() === formData.siteId);

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col">
      <header className="bg-white border-b border-slate-200 sticky top-0 z-30">
        <div className="max-w-7xl mx-auto px-4 h-16 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link href="/admin/dashboard" className="p-2 -ml-2 rounded-lg hover:bg-slate-100 text-slate-500 hover:text-slate-900 transition-colors">
              <ChevronLeft className="w-5 h-5" />
            </Link>
            <div className="flex items-center gap-2 text-primary">
              <Wrench className="w-5 h-5" />
              <h1 className="font-bold text-lg text-slate-900">Engineer Jobs</h1>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <span className="text-sm font-medium text-slate-600 hidden sm:inline-block">Admin: {user?.name}</span>
            <button onClick={logout} className="text-sm font-medium text-red-600 hover:bg-red-50 px-3 py-1.5 rounded-lg transition-colors">Logout</button>
          </div>
        </div>
      </header>

      <main className="flex-1 max-w-7xl w-full mx-auto p-4 md:p-6 lg:p-8 space-y-6">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Manage Jobs</h1>
            <p className="text-sm text-slate-500 mt-1">Assign and track callouts and site surveys</p>
          </div>
          {activeEngineers.length === 0 || activeSites.length === 0 ? (
            <div className="flex items-center gap-2 px-3 py-1.5 bg-amber-50 border border-amber-200 rounded-lg text-amber-800 text-xs font-medium">
              <AlertTriangle className="w-4 h-4" /> 
              <span>Setup required: </span>
              {activeEngineers.length === 0 && <Link href="/admin/engineer-ppm" className="underline hover:text-amber-900">Add Engineers</Link>}
              {activeEngineers.length === 0 && activeSites.length === 0 && <span> & </span>}
              {activeSites.length === 0 && <Link href="/admin/sites" className="underline hover:text-amber-900">Add Sites</Link>}
            </div>
          ) : (
            <button onClick={handleCreateOpen} className="flex items-center justify-center gap-2 bg-primary hover:bg-primary/90 text-white px-4 py-2.5 rounded-lg text-sm font-medium transition-colors">
              <Plus className="w-4 h-4" /> Assign New Job
            </button>
          )}
        </div>

        <div className="bg-white border border-slate-200 rounded-xl p-4 flex flex-col md:flex-row gap-4">
          <div className="relative flex-1">
            <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
            <input 
              type="text" 
              placeholder="Search jobs, sites, engineers..." 
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
              className="w-full pl-9 pr-4 py-2 bg-slate-50 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
            />
          </div>
          <div className="flex gap-2 overflow-x-auto pb-2 md:pb-0">
            <select 
              value={kindFilter} 
              onChange={e => setKindFilter(e.target.value)}
              className="px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 min-w-max"
            >
              <option value="all">All Types</option>
              <option value="callout">Call Outs</option>
              <option value="site_survey">Site Surveys</option>
            </select>
            <select 
              value={statusFilter} 
              onChange={e => setStatusFilter(e.target.value)}
              className="px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 min-w-max"
            >
              <option value="all">All Statuses</option>
              <option value="assigned">Assigned</option>
              <option value="in_progress">In Progress</option>
              <option value="submitted">Submitted</option>
              <option value="cancelled">Cancelled</option>
            </select>
            <select 
              value={priorityFilter} 
              onChange={e => setPriorityFilter(e.target.value)}
              className="px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 min-w-max"
            >
              <option value="all">All Priorities</option>
              <option value="normal">Normal</option>
              <option value="urgent">Urgent</option>
            </select>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {filteredJobs.length === 0 ? (
            <div className="col-span-full py-12 text-center text-slate-500 bg-white border border-slate-200 rounded-xl">
              No jobs found matching your filters.
            </div>
          ) : (
            filteredJobs.map(job => (
              <div key={job.id} className="bg-white border border-slate-200 rounded-xl overflow-hidden hover:shadow-md transition-shadow flex flex-col">
                <div className="p-5 border-b border-slate-100 flex-1">
                  <div className="flex items-start justify-between gap-3 mb-3">
                    <div className="flex flex-wrap gap-2">
                      {job.kind === "callout" ? (
                        <span className="flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-rose-50 text-rose-700 border border-rose-200">
                          <AlertTriangle className="w-3 h-3" /> Call Out
                        </span>
                      ) : (
                        <span className="flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-blue-50 text-blue-700 border border-blue-200">
                          <Map className="w-3 h-3" /> Survey
                        </span>
                      )}
                      
                      {job.priority === "urgent" && (
                        <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-amber-50 text-amber-700 border border-amber-200">
                          Urgent
                        </span>
                      )}
                    </div>
                    
                    {job.status === "assigned" && <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded text-blue-700 bg-blue-50">Assigned</span>}
                    {job.status === "in_progress" && <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded text-amber-700 bg-amber-50">In Progress</span>}
                    {job.status === "submitted" && <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded text-emerald-700 bg-emerald-50">Submitted</span>}
                    {job.status === "cancelled" && <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded text-slate-500 bg-slate-100">Cancelled</span>}
                  </div>
                  
                  <h3 className="font-bold text-slate-900 text-lg mb-1 leading-tight flex items-center flex-wrap gap-2">
                    {job.site.name}
                    {job.site.brand ? <span className="px-1.5 py-0.5 bg-slate-100 text-slate-600 text-[10px] rounded font-bold uppercase tracking-wider">{job.site.brand}</span> : null}
                  </h3>
                  <p className="text-sm font-medium text-slate-700 mb-3">{job.title}</p>
                  
                  <div className="space-y-2 text-sm text-slate-600">
                    <div className="flex items-center gap-2">
                      <Calendar className="w-4 h-4 text-slate-400" />
                      <span>{new Date(job.scheduledDate).toLocaleDateString()}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Wrench className="w-4 h-4 text-slate-400" />
                      <span>{job.engineerName}</span>
                    </div>
                  </div>
                </div>
                
                <div className="px-5 py-3 bg-slate-50 flex items-center justify-between border-t border-slate-200">
                  <div className="flex gap-2">
                     <button 
                       onClick={() => handleEditOpen(job)} 
                       className="text-xs font-medium text-slate-600 hover:text-primary"
                     >
                       Edit
                     </button>
                     
                     {job.status === "submitted" || job.status === "cancelled" ? (
                       <button 
                         onClick={() => handleStatusChange(job, "in_progress")} 
                         className="text-xs font-medium text-amber-600 hover:text-amber-700 flex items-center gap-1"
                       >
                         <RefreshCw className="w-3 h-3" /> Reopen
                       </button>
                     ) : (
                       <button 
                         onClick={() => {
                           if(confirm("Are you sure you want to cancel this job?")) {
                             handleStatusChange(job, "cancelled");
                           }
                         }} 
                         className="text-xs font-medium text-red-600 hover:text-red-700 flex items-center gap-1"
                       >
                         <X className="w-3 h-3" /> Cancel
                       </button>
                     )}
                  </div>
                  <button 
                    onClick={() => setLocation(`/engineer/jobs/${job.id}`)}
                    className="text-xs font-semibold text-primary hover:text-primary/80 flex items-center gap-1"
                  >
                    View Job <ChevronRight className="w-3 h-3" />
                  </button>
                </div>
              </div>
            ))
          )}
        </div>
      </main>

      {isCreating && (
        <div className="fixed inset-0 bg-slate-900/50 z-50 flex items-center justify-center p-4 sm:p-6 overflow-y-auto">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-2xl max-h-full flex flex-col">
            <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between sticky top-0 bg-white z-10 rounded-t-xl">
              <h2 className="text-xl font-bold text-slate-900">{editingJob ? 'Edit Job' : 'Assign New Job'}</h2>
              <button onClick={() => setIsCreating(false)} className="p-2 -mr-2 rounded-lg hover:bg-slate-100 text-slate-500">
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <div className="p-6 overflow-y-auto flex-1 space-y-6">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-medium text-slate-600 mb-1.5 uppercase tracking-wider">Job Type</label>
                  <select 
                    value={formData.kind}
                    onChange={e => setFormData(prev => ({ ...prev, kind: e.target.value as any }))}
                    disabled={!!editingJob}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 disabled:bg-slate-50 disabled:opacity-70"
                  >
                    <option value="callout">Call Out</option>
                    <option value="site_survey">Site Survey</option>
                  </select>
                </div>
                
                <div>
                  <label className="block text-xs font-medium text-slate-600 mb-1.5 uppercase tracking-wider">Priority</label>
                  <select 
                    value={formData.priority}
                    onChange={e => setFormData(prev => ({ ...prev, priority: e.target.value as any }))}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
                  >
                    <option value="normal">Normal</option>
                    <option value="urgent">Urgent</option>
                  </select>
                </div>
              </div>
              
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-medium text-slate-600 mb-1.5 uppercase tracking-wider">Site Brand</label>
                  <select 
                    value={formData.brand}
                    onChange={e => setFormData(prev => ({ ...prev, brand: e.target.value, siteId: "" }))}
                    disabled={!!editingJob}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 disabled:bg-slate-50 disabled:opacity-70"
                  >
                    <option value="">No Brand</option>
                    {BRANDS.map(b => <option key={b} value={b}>{b}</option>)}
                    {formData.brand && !BRANDS.includes(formData.brand as any) && (
                      <option value={formData.brand}>{formData.brand} (Archived)</option>
                    )}
                  </select>
                </div>
                
                <div>
                  <label className="block text-xs font-medium text-slate-600 mb-1.5 uppercase tracking-wider">Site</label>
                  <select 
                    value={formData.siteId}
                    onChange={e => setFormData(prev => ({ ...prev, siteId: e.target.value }))}
                    disabled={!!editingJob}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 disabled:bg-slate-50 disabled:opacity-70"
                  >
                    <option value="" disabled>Select a site</option>
                    {activeSites.filter(s => (s.brand || "") === formData.brand).map(s => (
                      <option key={s.id} value={s.id.toString()}>{s.name} - {s.address}</option>
                    ))}
                    {editingJob && !activeSites.find(s => s.id === editingJob.siteId) && (
                       <option value={editingJob.siteId.toString()}>{editingJob.site.name} (Archived)</option>
                    )}
                  </select>
                  
                  {/* Site Preview */}
                  {!editingJob && selectedSite && (
                    <div className="mt-3 bg-slate-50 border border-slate-200 rounded-lg p-3 flex gap-4 items-center">
                      {selectedSite.coverImagePath ? (
                        <div className="w-16 h-16 rounded bg-slate-200 shrink-0 overflow-hidden">
                          <img src={`${import.meta.env.BASE_URL}${selectedSite.coverImagePath.replace(/^\//, "")}`} className="w-full h-full object-cover" alt="Cover" />
                        </div>
                      ) : (
                        <div className="w-16 h-16 rounded bg-slate-200 shrink-0 flex items-center justify-center">
                          <Map className="w-6 h-6 text-slate-400" />
                        </div>
                      )}
                      <div className="text-sm">
                        <p className="font-semibold text-slate-900">{selectedSite.name}</p>
                        {selectedSite.contactName && <p className="text-slate-600">Contact: {selectedSite.contactName}</p>}
                        {selectedSite.phone && <p className="text-slate-600">{selectedSite.phone}</p>}
                      </div>
                    </div>
                  )}
                  {editingJob && (
                    <div className="mt-3 bg-slate-50 border border-slate-200 rounded-lg p-3 text-sm text-slate-600">
                       <p className="font-semibold text-slate-900">{editingJob.site.name}</p>
                       <p>{editingJob.site.address}</p>
                    </div>
                  )}
                </div>
              </div>
              
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1.5 uppercase tracking-wider">Job Title</label>
                <input 
                  type="text" 
                  value={formData.title}
                  onChange={e => setFormData(prev => ({ ...prev, title: e.target.value }))}
                  placeholder="e.g. AC Unit Not Cooling"
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
                />
              </div>
              
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1.5 uppercase tracking-wider">Description / Instructions</label>
                <textarea 
                  value={formData.description}
                  onChange={e => setFormData(prev => ({ ...prev, description: e.target.value }))}
                  rows={4}
                  placeholder="Details of the issue or survey requirements..."
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 resize-y"
                />
              </div>
              
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-medium text-slate-600 mb-1.5 uppercase tracking-wider">Scheduled Date</label>
                  <input 
                    type="date" 
                    value={formData.scheduledDate}
                    onChange={e => setFormData(prev => ({ ...prev, scheduledDate: e.target.value }))}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
                  />
                </div>
                
                <div>
                  <label className="block text-xs font-medium text-slate-600 mb-1.5 uppercase tracking-wider">Assign Engineer</label>
                  <select 
                    value={formData.engineerId}
                    onChange={e => setFormData(prev => ({ ...prev, engineerId: e.target.value }))}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
                  >
                    <option value="" disabled>Select an engineer</option>
                    {activeEngineers.map(e => (
                      <option key={e.id} value={e.id.toString()}>{e.name}</option>
                    ))}
                    {editingJob && !activeEngineers.find(e => e.id === editingJob.engineerId) && (
                       <option value={editingJob.engineerId.toString()}>{editingJob.engineerName} (Archived)</option>
                    )}
                  </select>
                </div>
              </div>
            </div>
            
            <div className="px-6 py-4 border-t border-slate-200 flex justify-end gap-3 rounded-b-xl bg-slate-50 sticky bottom-0">
              <button 
                onClick={() => setIsCreating(false)} 
                className="px-4 py-2 text-sm font-medium text-slate-600 hover:text-slate-900 bg-white border border-slate-300 rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button 
                onClick={handleSave} 
                disabled={createJob.isPending || updateJob.isPending}
                className="px-4 py-2 text-sm font-medium text-white bg-primary hover:bg-primary/90 rounded-lg flex items-center gap-2 transition-colors disabled:opacity-50 shadow-sm"
              >
                {(createJob.isPending || updateJob.isPending) && <Loader2 className="w-4 h-4 animate-spin" />}
                {editingJob ? "Save Changes" : "Assign Job"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
